// src/client/hooks/useCrudMutations.ts
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from '@client/lib/api-client';
import { useToast } from '@client/hooks/use-toast';

interface UseCrudMutationsOptions {
  endpoint: string;
  queryKey: string[];
  entityName: string; // e.g., "Selector", "Setting", "Category"
}

export function useCrudMutations<T = any>({
  endpoint,
  queryKey,
  entityName,
}: UseCrudMutationsOptions) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<T>) => ApiClient.post<T>(endpoint, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: `${entityName} erstellt`,
        description: `${entityName} wurde erfolgreich hinzugefügt.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Fehler',
        description: error.message || `${entityName} konnte nicht erstellt werden.`,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<T> }) =>
      ApiClient.put<T>(`${endpoint}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: `${entityName} aktualisiert`,
        description: `${entityName} wurde erfolgreich geändert.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Fehler',
        description: error.message || `${entityName} konnte nicht aktualisiert werden.`,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => ApiClient.delete(`${endpoint}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: `${entityName} gelöscht`,
        description: `${entityName} wurde erfolgreich entfernt.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Fehler',
        description: error.message || `${entityName} konnte nicht gelöscht werden.`,
        variant: 'destructive',
      });
    },
  });

  // Toggle active mutation (optional)
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string | number; isActive: boolean }) =>
      ApiClient.put(`${endpoint}/${id}`, { isActive }),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: isActive ? `${entityName} aktiviert` : `${entityName} deaktiviert`,
        description: `${entityName} wurde erfolgreich ${isActive ? 'aktiviert' : 'deaktiviert'}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Fehler',
        description: error.message || 'Status konnte nicht geändert werden.',
        variant: 'destructive',
      });
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    toggleActiveMutation,
  };
}